<script setup lang="ts">
import type { AlbumListFilters } from "../AlbumFiltersDrawer/types"
import { refDebounced } from "@vueuse/core"
import { reactive, ref, shallowRef, toRaw, watchEffect } from "vue"
import { AlbumFiltersDrawer, AlbumSortModal } from "@/components"
import { Badge, Button, Input } from "@/components/ui"
import { useModal, useTranslation } from "@/composables"
import { AlbumSortModalKey } from "@/symbols"

interface AlbumListActionsProps {
  count?: number
  filters: AlbumListFilters
}

const props = withDefaults(defineProps<AlbumListActionsProps>(), { count: 0 })

const emit = defineEmits<{ "update:filters": [filters: AlbumListFilters] }>()

const internalFilters = reactive(structuredClone(toRaw(props.filters)))

const search = shallowRef("")
const debouncedSearch = refDebounced(search, 500)

const { t } = useTranslation()

const { toggle: toggleSortModal } = useModal(AlbumSortModalKey)
const isFiltersOpen = ref(false)

function onFiltersUpdate(filters: AlbumListFilters) {
  emit("update:filters", filters)
}

watchEffect(() => {
  internalFilters.search = debouncedSearch.value
  emit("update:filters", internalFilters)
})
</script>

<template>
  <div>
    <div class="flex gap-2 mb-3">
      <Button variant="tertiary" prefix-icon="ArrowDownUp" @click="toggleSortModal">
        {{ t('album_list_actions.sort') }}
      </Button>
      <Badge :count="count">
        <Button prefix-icon="Filter" variant="tertiary" @click="isFiltersOpen = !isFiltersOpen">
          <span class="sr-only md:not-sr-only">
            {{ t('filter') }}
          </span>
        </Button>
      </Badge>
    </div>

    <div class="w-full">
      <Input v-model="search" name="search" :label="t('album_list_actions.search_label')" clearable />
    </div>
  </div>

  <AlbumFiltersDrawer v-model:open="isFiltersOpen" :filters="filters" @update:filters="onFiltersUpdate" />

  <AlbumSortModal />
</template>
